import React, { memo, useMemo } from "react";
import * as THREE from "three";

import { MeasureKind, UnitsByMeasure } from "convert";
import { useIntl } from 'react-intl';
import { RoomDetailsIndicatorProps } from "../../../@types/Components";
import { EUnitType } from '../../../@types/Enums';
import { useExportContext } from "../../../provider/ExportProvider";
import { useUnitContext } from '../../../provider/UnitProvider';
import { useLanguage } from "../../../provider/LanguageProvider";
import { useZoomChangeContext } from "../../../provider/ZoomChangeProvider";
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import {
  calculateVerticalTextShiftForExports,
  calculateVerticalTextShiftFromZoom
} from "../../../utils/fontUtils";
import { localizeNumber } from "../../../utils/numberUtils";
import RENDER_ORDERS from '../../../utils/renderOrder';
import CustomTextComponent from "../CustomTextComponent";

const RoomDetailsIndicator = ({
  roomName,
  roomArea,
  roomPerimeter,
  centroid,
}: RoomDetailsIndicatorProps): React.JSX.Element => {
  const { locale } = useLanguage();
  const { staggeredZoomData } = useZoomChangeContext();
  const { isExporting, exportCameraHeight, exportCameraWidth } =
    useExportContext();
  const { unitType } = useUnitContext();
  const fontSize = 1.0;

  const intl = useIntl();
  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "meter"), [intl]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "foot"), [intl]);

  const textOffsets = useMemo(() => {
    if (isExporting) {
      return calculateVerticalTextShiftForExports(
        exportCameraHeight,
        exportCameraWidth,
        !!roomName
      );
    }

    return calculateVerticalTextShiftFromZoom(staggeredZoomData, !!roomName);
  }, [staggeredZoomData, isExporting, roomName, exportCameraHeight, exportCameraWidth]);

  // TODO correct math labeling for area and perimeter in all languages
  const perimeterLabel = useMemo(() => (locale === "de" ? "U" : "P"), [locale]);

  return (
    <group
      name={`group-roomDetailsIndicator-room=${roomName || "Unnamed"}`}
      renderOrder={RENDER_ORDERS.roomDetailsIndicator}
    >
      {/* Title */}
      {roomName && (
        <CustomTextComponent
          text={roomName}
          fontSize={fontSize}
          color={"#000"}
          position={new THREE.Vector3(centroid.x, centroid.y + textOffsets[0], 0)}
          staggeredZoomData={staggeredZoomData}
        />
      )}
      {/* Area */}
      <CustomTextComponent
        text={
          unitType === EUnitType.METRIC
            ? `A = ${localizeNumber(roomArea.toString(), locale)}${mLocalizedLabel}²`
            : `A = ${localizeNumber(
                convertTo(
                  roomArea,
                  "m2" as UnitsByMeasure<MeasureKind.Area>,
                  "ft2" as UnitsByMeasure<MeasureKind.Area>
                ).toFixed(2),
                locale
              )}${ftLocalizedLabel}²`
        }
        fontSize={fontSize}
        color={"#000"}
        position={new THREE.Vector3(centroid.x, centroid.y + textOffsets[1], 0)}
        staggeredZoomData={staggeredZoomData}
      />
      {/* Perimeter */}
      <CustomTextComponent
        text={
          unitType === EUnitType.METRIC
            ? `${perimeterLabel} = ${localizeNumber(roomPerimeter.toString(), locale)}${mLocalizedLabel}`
            : `${perimeterLabel} = ${localizeNumber(
                convertTo(
                  roomPerimeter,
                  "meters" as UnitsByMeasure<MeasureKind.Length>,
                  "feet" as UnitsByMeasure<MeasureKind.Length>
                ).toFixed(2),
                locale
              )}${ftLocalizedLabel}`
        }
        fontSize={fontSize}
        color={"#000"}
        position={new THREE.Vector3(
          centroid.x,
          centroid.y + textOffsets[2],
          0
        )}
        staggeredZoomData={staggeredZoomData}
      />
    </group>
  );
};

export default memo(RoomDetailsIndicator);
